import React, { useState, useEffect, useRef } from 'react';
import { 
  Vote, Check, X, Minus, Upload, Users, Search,
  FileText, Camera, RefreshCw, AlertCircle, CheckCircle,
  ChevronDown, Eye, Edit2, Trash2, Filter, Mail, Bell
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component for gestionnaire to manage VPC (paper ballots, modifications, etc.)
const VPCGestionnairePanel = ({ 
  assemblee, 
  token, 
  onUpdate 
}) => {
  const [copros, setCopros] = useState([]);
  const [vpcVotes, setVpcVotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCopro, setSelectedCopro] = useState(null);
  const [resolutions, setResolutions] = useState([]);
  const [manualVotes, setManualVotes] = useState({});
  const [showOCR, setShowOCR] = useState(false);
  const [ocrImage, setOcrImage] = useState(null);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, voted, not_voted
  
  const fileInputRef = useRef(null);
  
  // Load data
  useEffect(() => {
    if (!assemblee?.id || !token) return;
    loadData();
  }, [assemblee?.id, token]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      // Load coproprietaires
      const coprosRes = await axios.get(
        `${API}/assemblees/${assemblee.id}/vpc/copros`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCopros(coprosRes.data.copros || []);
      
      // Load all VPC votes
      const vpcRes = await axios.get(
        `${API}/assemblees/${assemblee.id}/vpc/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVpcVotes(vpcRes.data.votes || []);
      setStats(vpcRes.data.stats);
      
      // Load resolutions
      const resRes = await axios.get(
        `${API}/assemblees/${assemblee.id}/vpc/resolutions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResolutions(resRes.data.resolutions || []);
      
    } catch (err) {
      console.error('Error loading VPC data:', err);
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter copros
  const filteredCopros = copros.filter(c => {
    const matchesSearch = searchQuery === '' || 
      `${c.prenom} ${c.nom}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'voted' && c.vpc_status) ||
      (filterStatus === 'not_voted' && !c.vpc_status);
    
    return matchesSearch && matchesFilter;
  });
  
  // Select copro for manual entry
  const handleSelectCopro = (copro) => {
    setSelectedCopro(copro);
    
    // Load existing votes if any
    const existingVpc = vpcVotes.find(v => v.user_id === copro.id);
    if (existingVpc) {
      setManualVotes(existingVpc.votes || {});
    } else {
      setManualVotes({});
    }
    
    setOcrResult(null);
    setOcrImage(null);
  };
  
  // Handle manual vote
  const handleManualVote = (resId, voteType) => {
    setManualVotes(prev => ({
      ...prev,
      [resId]: voteType
    }));
  };
  
  // Vote all manually
  const handleVoteAllManual = (voteType) => {
    const newVotes = {};
    resolutions.forEach(r => {
      newVotes[r.id] = voteType;
    });
    setManualVotes(newVotes);
  };
  
  // Save manual/paper votes
  const handleSaveVotes = async (source = 'paper') => {
    if (!selectedCopro || Object.keys(manualVotes).length === 0) {
      setError('Veuillez sélectionner un coproprietaire et voter sur les résolutions');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      await axios.post(
        `${API}/assemblees/${assemblee.id}/vpc/paper`,
        {
          copro_user_id: selectedCopro.id,
          votes: manualVotes,
          source: source
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(`Vote papier enregistré pour ${selectedCopro.prenom} ${selectedCopro.nom}`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Reload data
      loadData();
      
      // Reset
      setSelectedCopro(null);
      setManualVotes({});
      setOcrResult(null);
      setOcrImage(null);
      
      if (onUpdate) onUpdate();
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle image upload for OCR
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setOcrImage(base64);
      
      // Analyze with OCR
      setOcrLoading(true);
      setOcrResult(null);
      setError(null);
      
      try {
        const response = await axios.post(
          `${API}/assemblees/${assemblee.id}/vpc/analyze-paper`,
          { image: base64 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          setOcrResult(response.data);
          setManualVotes(response.data.votes || {});
          setSuccess(`Analyse OCR réussie (confiance: ${Math.round(response.data.confidence * 100)}%)`);
        } else {
          setError(response.data.error || 'Erreur d\'analyse');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Erreur lors de l\'analyse OCR');
      } finally {
        setOcrLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };
  
  // Send reminders to non-voters
  const handleSendReminders = async () => {
    setSendingReminders(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${API}/assemblees/${assemblee.id}/vpc/send-reminders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(null), 5000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi des rappels');
    } finally {
      setSendingReminders(false);
    }
  };
  
  // Calculate non-voters count
  const nonVotersCount = copros.filter(c => !c.vpc_status).length;
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="animate-spin text-[#00D3A8] mr-2" size={24} />
        <span className="text-gray-600">Chargement...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="vpc-gestionnaire-panel">
      {/* Reminder button */}
      {nonVotersCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className="text-amber-600" size={24} />
            <div>
              <p className="font-medium text-amber-800">
                {nonVotersCount} coproprietaire(s) n'ont pas encore voté
              </p>
              <p className="text-sm text-amber-600">
                Envoyez un rappel par email pour augmenter la participation
              </p>
            </div>
          </div>
          <button
            onClick={handleSendReminders}
            disabled={sendingReminders}
            className="flex items-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
          >
            {sendingReminders ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Mail size={18} />
                <span>Envoyer rappels</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Total VPC</p>
            <p className="text-2xl font-bold text-[#00D3A8]">{stats.total_vpc}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">VPC Digital</p>
            <p className="text-2xl font-bold text-blue-600">{stats.digital_vpc}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">VPC Papier</p>
            <p className="text-2xl font-bold text-orange-600">{stats.paper_vpc}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Tantièmes VPC</p>
            <p className="text-2xl font-bold text-purple-600">{stats.total_tantiemes_vpc}</p>
          </div>
        </div>
      )}
      
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Copropriétaires list */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
              <Users size={20} className="text-[#00D3A8]" />
              <span>Copropriétaires</span>
            </h3>
            
            {/* Search and filter */}
            <div className="mt-3 flex space-x-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="all">Tous</option>
                <option value="voted">A voté</option>
                <option value="not_voted">N'a pas voté</option>
              </select>
            </div>
          </div>
          
          <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100">
            {filteredCopros.map(copro => (
              <button
                key={copro.id}
                onClick={() => handleSelectCopro(copro)}
                className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                  selectedCopro?.id === copro.id ? 'bg-[#00D3A8]/10 border-l-4 border-[#00D3A8]' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-800">
                      {copro.prenom} {copro.nom}
                    </p>
                    <p className="text-xs text-gray-500">{copro.email}</p>
                    <p className="text-xs text-gray-400">{copro.tantiemes_immeuble} tantièmes</p>
                  </div>
                  <div className="text-right">
                    {copro.vpc_status ? (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        copro.vpc_status === 'signed' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {copro.vpc_source === 'digital' ? '🖥️' : '📄'} 
                        {copro.vpc_status === 'signed' ? 'Signé' : 'Validé'}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500">
                        Pas de vote
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
            
            {filteredCopros.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                Aucun coproprietaire trouvé
              </div>
            )}
          </div>
        </div>
        
        {/* Right: Vote entry panel */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
              <Vote size={20} className="text-[#00D3A8]" />
              <span>
                {selectedCopro 
                  ? `Saisie VPC - ${selectedCopro.prenom} ${selectedCopro.nom}`
                  : 'Saisie Vote par Correspondance'
                }
              </span>
            </h3>
          </div>
          
          {!selectedCopro ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="mx-auto mb-2 text-gray-300" size={32} />
              <p>Sélectionnez un coproprietaire dans la liste</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {/* OCR Upload */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-blue-800 flex items-center space-x-2">
                    <Camera size={18} />
                    <span>Scan bulletin papier (OCR)</span>
                  </p>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={ocrLoading}
                  className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:bg-blue-100 transition-colors flex items-center justify-center space-x-2"
                >
                  {ocrLoading ? (
                    <>
                      <RefreshCw className="animate-spin" size={18} />
                      <span>Analyse en cours...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      <span>Charger une image du bulletin</span>
                    </>
                  )}
                </button>
                
                {ocrResult && (
                  <div className="mt-2 text-sm text-blue-700">
                    <p>✅ Votes détectés - Confiance: {Math.round(ocrResult.confidence * 100)}%</p>
                    {ocrResult.notes && <p className="text-xs">{ocrResult.notes}</p>}
                  </div>
                )}
              </div>
              
              {/* Quick vote buttons */}
              <div className="flex space-x-2">
                <button
                  onClick={() => handleVoteAllManual('pour')}
                  className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600"
                >
                  Tout POUR
                </button>
                <button
                  onClick={() => handleVoteAllManual('contre')}
                  className="flex-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600"
                >
                  Tout CONTRE
                </button>
                <button
                  onClick={() => handleVoteAllManual('abstention')}
                  className="flex-1 px-3 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600"
                >
                  Tout ABS
                </button>
              </div>
              
              {/* Resolutions */}
              <div className="max-h-[250px] overflow-y-auto space-y-2">
                {resolutions.map((res, idx) => (
                  <div key={res.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {idx + 1}. {res.titre}
                      </p>
                    </div>
                    <div className="flex space-x-1 ml-2">
                      {['pour', 'contre', 'abstention'].map(type => (
                        <button
                          key={type}
                          onClick={() => handleManualVote(res.id, type)}
                          className={`p-1.5 rounded ${
                            manualVotes[res.id] === type
                              ? type === 'pour' ? 'bg-green-500 text-white'
                              : type === 'contre' ? 'bg-red-500 text-white'
                              : 'bg-gray-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {type === 'pour' ? <Check size={14} /> 
                           : type === 'contre' ? <X size={14} />
                           : <Minus size={14} />}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Save button */}
              <button
                onClick={() => handleSaveVotes(ocrResult ? 'paper_ocr' : 'paper')}
                disabled={saving || Object.keys(manualVotes).length === 0}
                className="w-full px-4 py-3 bg-[#00D3A8] text-white rounded-lg font-medium hover:bg-[#00C7A1] disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    <span>Enregistrement...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={18} />
                    <span>Valider le vote papier</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* VPC Votes summary */}
      {vpcVotes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-800 flex items-center space-x-2">
              <Eye size={20} className="text-[#00D3A8]" />
              <span>Votes par correspondance reçus ({vpcVotes.length})</span>
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600">Copropriétaire</th>
                  <th className="px-4 py-2 text-center text-gray-600">Tantièmes</th>
                  <th className="px-4 py-2 text-center text-gray-600">Source</th>
                  <th className="px-4 py-2 text-center text-gray-600">Statut</th>
                  <th className="px-4 py-2 text-center text-gray-600">Pour</th>
                  <th className="px-4 py-2 text-center text-gray-600">Contre</th>
                  <th className="px-4 py-2 text-center text-gray-600">Abs.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vpcVotes.map(vpc => {
                  const votes = vpc.votes || {};
                  const pourCount = Object.values(votes).filter(v => v === 'pour').length;
                  const contreCount = Object.values(votes).filter(v => v === 'contre').length;
                  const absCount = Object.values(votes).filter(v => v === 'abstention').length;
                  
                  return (
                    <tr key={vpc.user_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {vpc.user_info?.prenom} {vpc.user_info?.nom}
                      </td>
                      <td className="px-4 py-2 text-center">{vpc.user_info?.tantiemes || 0}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          vpc.source === 'digital' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {vpc.source === 'digital' ? '🖥️ Digital' : '📄 Papier'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                          {vpc.status === 'signed' ? 'Signé' : 'Validé'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center text-green-600 font-medium">{pourCount}</td>
                      <td className="px-4 py-2 text-center text-red-600 font-medium">{contreCount}</td>
                      <td className="px-4 py-2 text-center text-gray-600 font-medium">{absCount}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default VPCGestionnairePanel;
